import api, { getErrorMessage } from './api';

const ssoService = {
  // ---------------------------------------------------------------------------
  // Admin endpoints (superuser only, org inferred from JWT)
  // ---------------------------------------------------------------------------

  /**
   * Get SSO configuration for the admin's organization
   * @returns {Promise<object>} - SSO config (no client_secret)
   */
  getConfig: async () => {
    try {
      const response = await api.get('/api/v1/admin/sso/configurations');
      return response.data;
    } catch (error) {
      // 404 means SSO is not configured for this org — return null
      // instead of throwing so the UI can render the "not configured" state.
      if (error?.response?.status === 404) {
        return null;
      }
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Create SSO configuration for the admin's organization
   * @param {object} data - SSO config data
   * @param {string} data.provider_type - "azure_ad" or "okta"
   * @param {string} data.client_id - OIDC client ID
   * @param {string} data.client_secret - OIDC client secret
   * @param {string} [data.tenant_id] - Azure AD tenant ID (required for azure_ad)
   * @param {string} [data.okta_domain] - Okta domain (required for okta)
   * @param {string} data.redirect_uri - OAuth redirect URI
   * @param {boolean} data.is_enabled - Whether SSO is enabled
   * @returns {Promise<object>} - Created SSO config (no client_secret)
   */
  createConfig: async (data) => {
    try {
      const response = await api.post('/api/v1/admin/sso/configurations', data);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Update SSO configuration for the admin's organization
   * @param {object} data - SSO config data (same shape as create)
   * @returns {Promise<object>} - Updated SSO config (no client_secret)
   */
  updateConfig: async (data) => {
    try {
      const response = await api.put('/api/v1/admin/sso/configurations', data);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Delete SSO configuration for the admin's organization
   * @returns {Promise<object>} - Deletion confirmation
   */
  deleteConfig: async () => {
    try {
      const response = await api.delete('/api/v1/admin/sso/configurations');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  // ---------------------------------------------------------------------------
  // Public endpoints (for login flow, no auth required)
  // ---------------------------------------------------------------------------

  /**
   * Check whether an email's organization uses SSO
   * @param {string} email - User email address
   * @returns {Promise<object>} - { auth_method, sso_enabled, organization_id, provider_type }
   */
  checkEmail: async (email) => {
    const response = await api.post('/api/v1/auth/check-email', { email });
    return response.data;
  },

  /**
   * Get minimal SSO config info for a given organization (no secrets)
   * @param {string} orgId - Organization UUID
   * @returns {Promise<object>} - { sso_enabled, provider_type }
   */
  getPublicConfig: async (orgId) => {
    try {
      const response = await api.get(`/api/v1/sso/${orgId}/config`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Get the SSO authorization URL for a given organization
   * @param {string} orgId - Organization UUID
   * @returns {Promise<object>} - { authorization_url }
   */
  getAuthorizeUrl: async (orgId) => {
    try {
      const response = await api.get(`/api/v1/sso/${orgId}/authorize`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Exchange a one-time SSO code for an access token
   * @param {string} code - One-time SSO code from callback
   * @returns {Promise<object>} - { access_token }
   */
  exchangeToken: async (code) => {
    try {
      const response = await api.post('/api/v1/sso/token-exchange', { code });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Set a new password for first-time password users in mixed-auth orgs.
   * Requires authentication (must_change_password flag set in JWT).
   * @param {string} newPassword - The new password to set
   * @returns {Promise<object>} - { access_token, token_type }
   */
  setPassword: async (newPassword) => {
    try {
      const response = await api.post('/api/v1/auth/set-password', { new_password: newPassword });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },
};

export default ssoService;
